import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useBackdropContext } from '../../../../../context/backdrop';
import { useInstallationContext } from '../../../../../context/installation';
import {
  DashboardItemType,
  ModifyGroupItemInMultipleGroupsMutation,
  ModifyGroupItemInMultipleGroupsMutationVariables,
  UserPermissionType,
} from '../../../../../data-access/gql-types/graphql';
import { MODIFY_GROUP_ITEM_IN_MULTIPLE_GROUPS } from '../../../../../data-access/mutations/groups';
import { useMutationErrors } from '../../../../../hooks/errors/use-mutation-errors';
import { useRefetchData } from '../../../../../hooks/refresh-data/use-refetch-data';
import { useUserPermissions } from '../../../../../hooks/user/use-user-permissions';
import { MutationErrorType } from '../../../../../types';
import { toastError, toastSuccess } from '../../../../../utils/toast';
import { useGroups } from '../../../hooks/groups/use-groups';
import { GroupItem } from '../types';

type ModifyMultipleGroupsParams = {
  itemId: string;
  itemType: DashboardItemType;
};

export const useModifyMultipleGroups = ({ itemId, itemType }: ModifyMultipleGroupsParams) => {
  const { t } = useTranslation('common');
  const [groupPopup, setGroupPopup] = useState<boolean>(false);
  const [groupList, setGroupList] = useState<GroupItem[]>([]);
  const { groups } = useGroups();
  const { permissions } = useUserPermissions();
  const { selectedInstallationId } = useInstallationContext();
  const { refetchGroups } = useRefetchData();
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const [modifyGroup] = useMutation<
    ModifyGroupItemInMultipleGroupsMutation,
    ModifyGroupItemInMultipleGroupsMutationVariables
  >(MODIFY_GROUP_ITEM_IN_MULTIPLE_GROUPS);
  const { handleErrors } = useMutationErrors(MutationErrorType.Group);

  useEffect(() => {
    const canOperateOnPublicGroups = permissions[UserPermissionType.AddRemoveChannelForMultiplePublicGroups];

    const list = groups
      .filter((g) => (!canOperateOnPublicGroups ? !g.group.isPublicGroup : g.id))
      .map((group) => ({
        id: group.id,
        name: group.group.name,
        checked: !!group.userGroupItems.find((x) => x.itemId === itemId),
      }));

    setGroupList(list);
  }, [groupPopup, groups, permissions]);

  const toggleGroup = (id: string) => {
    setGroupList((prev) => {
      const foundIndex = prev.findIndex((x) => x.id === id);
      if (foundIndex !== -1) prev[foundIndex].checked = !prev[foundIndex].checked;
      return [...prev];
    });
  };

  const handleSaveGroup = () => {
    turnOnBackdrop();
    const addFiltered = groups.filter((group) => !group.userGroupItems.find((x) => x.itemId === itemId));
    const removeFiltered = groups.filter((group) => group.userGroupItems.find((x) => x.itemId === itemId));

    const groupIdsToAdd = groupList
      .filter((group) => group.checked && addFiltered.find((x) => x.id === group.id))
      .map((x) => x.id);
    const groupIdsToRemove = groupList
      .filter((group) => !group.checked && removeFiltered.find((x) => x.id === group.id))
      .map((x) => x.id);

    modifyGroup({
      variables: {
        input: {
          installationId: selectedInstallationId,
          itemId,
          itemType,
          groupIdsToAdd,
          groupIdsToRemove,
        },
      },
      onCompleted: (data) => {
        turnOffBackdrop();
        if (!data || !data.modifyGroupItemInMultipleGroups) {
          toastError({ content: t('errors.somethingWentWrong') });
          return;
        }

        if (data.modifyGroupItemInMultipleGroups.result?.ok) {
          refetchGroups();
          setGroupPopup(false);
          toastSuccess({ content: t('saved') });
        } else {
          handleErrors(data.modifyGroupItemInMultipleGroups.errors || []);
        }
      },
      onError: () => {
        turnOffBackdrop();
        toastError({ content: t('errors.somethingWentWrong') });
      },
    });
  };

  return {
    groupPopup,
    groupList,
    setGroupPopup,
    handleSaveGroup,
    toggleGroup,
  };
};
